import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface Seller {
  id: string;
  name: string;
  description: string;
  image_url: string;
  rating: number;
  address: string;
  created_at: string;
  is_open: boolean;
  opening_hours: any;
  average_rating?: number;
}

interface StockItem {
  id: string;
  name: string;
  description: string;
  price: number;
  restaurant_id: string;
  restaurant?: {
    name: string;
    address: string;
    is_open: boolean;
  };
}

interface SearchResults {
  sellers: Seller[];
  stockItems: StockItem[];
  loading: boolean;
}

export const useMarketplaceSearch = (query: string): SearchResults => {
  const [results, setResults] = useState<SearchResults>({
    sellers: [],
    stockItems: [],
    loading: false,
  });

  useEffect(() => {
    const searchData = async () => {
      if (!query || query.length < 2) {
        setResults({ sellers: [], stockItems: [], loading: false });
        return;
      }

      setResults(prev => ({ ...prev, loading: true }));

      try {
        // Search marketplace sellers
        const { data: sellerData, error: sellerError } = await supabase
          .from('restaurants')
          .select('id, name, description, image_url, rating, address, created_at, is_open, opening_hours')
          .eq('business_type', 'marketplace')
          .or(`name.ilike.%${query}%,description.ilike.%${query}%,address.ilike.%${query}%`)
          .limit(10);

        // Search stock items (menu items from marketplace sellers)
        const { data: stockItemData, error: stockItemError } = await supabase
          .from('menu_items')
          .select(`
            id, name, description, price, restaurant_id,
            restaurants!inner(name, address, is_open, business_type)
          `)
          .eq('restaurants.business_type', 'marketplace')
          .or(`name.ilike.%${query}%,description.ilike.%${query}%`)
          .eq('is_available', true)
          .limit(10);

        if (sellerError) throw sellerError;
        if (stockItemError) throw stockItemError;

        // Process seller data with defaults
        const processedSellers = (sellerData?.map(seller => ({
          ...seller,
          created_at: seller.created_at || new Date().toISOString(),
          is_open: seller.is_open ?? true,
          opening_hours: seller.opening_hours || {}
        })) || []).sort((a, b) => {
          const aHasLogo = a.image_url && a.image_url !== '/placeholder.svg' ? 1 : 0;
          const bHasLogo = b.image_url && b.image_url !== '/placeholder.svg' ? 1 : 0;
          return bHasLogo - aHasLogo;
        });

        // Process stock items data
        const processedStockItems = stockItemData?.map(item => ({
          ...item,
          restaurant: Array.isArray(item.restaurants) ? item.restaurants[0] : item.restaurants
        })) || [];

        setResults({
          sellers: processedSellers,
          stockItems: processedStockItems,
          loading: false,
        });
      } catch (error) {
        console.error('Marketplace search error:', error);
        setResults({ sellers: [], stockItems: [], loading: false });
      }
    };

    const debounceTimer = setTimeout(searchData, 300);
    return () => clearTimeout(debounceTimer);
  }, [query]);

  return results;
};
